import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection, useDataCollectionSource } from '@factorialco/f0-react/dist/experimental'
import { Handshake } from '@factorialco/f0-react/icons/app'
import {
  PerformanceProcessTargetAgreementCardAgreementFragment,
  PerformanceProcessTargetAgreementCardEsignaturesDocument,
  PerformanceProcessTargetAgreementCardEsignaturesFragment,
  PerformanceProcessTargetAgreementCardFragment,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { getSignaturesCompletionLabel } from 'modules/performance_process/components/agreements/DocumentCard'
import {
  agreementStatus,
  agreementStatusLabel,
  agreementStatusToVariant,
} from 'modules/performance_process/components/agreements/StatusTag'

interface NotInitiatedCardProps {
  target: PerformanceProcessTargetAgreementCardFragment
  onInitiate: () => void
}

interface Props {
  agreement: PerformanceProcessTargetAgreementCardAgreementFragment
  esignatures: PerformanceProcessTargetAgreementCardEsignaturesFragment[]
  url: string
}

const AgreementCard = ({ agreement, esignatures, url }: Props) => {
  const agreementDocument = agreement.agreementDocuments[0]
  const signatureStatus = agreementDocument?.signatureStatus

  const completitionLabel = signatureStatus
    ? getSignaturesCompletionLabel(esignatures)
    : i18n.t('performance_process.agreements.agreement_list_card.count', {
        count: [agreement.managerSignedAt, agreement.targetSignedAt].filter(Boolean).length,
        total: 2,
      })

  const dataSource = useDataCollectionSource({
    dataAdapter: {
      fetchData: async () => ({ records: [agreement] }),
    },
    itemUrl: () => url,
  })

  return (
    <OneDataCollection
      source={dataSource}
      visualizations={[
        {
          type: 'list',
          options: {
            fields: [
              {
                label: i18n.t('common.status'),
                render: () => {
                  const status = agreementStatus(agreement)
                  const label = agreementStatusLabel(status)
                  const variant = agreementStatusToVariant[status]

                  return {
                    type: 'status',
                    value: { label, status: variant },
                  }
                },
              },
            ],
            itemDefinition: () => ({
              title: i18n.t('performance_process.agreements.page.title'),
              description: completitionLabel ? [completitionLabel] : undefined,
              avatar: {
                type: 'icon',
                icon: Handshake,
              },
            }),
          },
        },
      ]}
    />
  )
}

const NotInitiatedCard = ({ target, onInitiate }: NotInitiatedCardProps) => {
  const canInitiateAgreement = target._policies?.initiate?.can

  const dataSource = useDataCollectionSource({
    dataAdapter: {
      fetchData: async () => ({ records: [target] }),
    },
    itemOnClick: () => () => canInitiateAgreement && onInitiate(),
    itemActions: () =>
      canInitiateAgreement
        ? [
            {
              label: i18n.t('performance_process.agreements.agreement_list_card.initiate'),
              type: 'primary' as const,
              onClick: onInitiate,
            },
          ]
        : [],
  })

  return (
    <Box cursor={canInitiateAgreement ? 'pointer' : 'default'}>
      <OneDataCollection
        source={dataSource}
        visualizations={[
          {
            type: 'list',
            options: {
              fields: [
                {
                  label: i18n.t('common.status'),
                  render: () => ({
                    type: 'status',
                    value: {
                      label: i18n.t(
                        'performance_process.agreements.my_agreements.category_not_started'
                      ),
                      status: 'neutral',
                    },
                  }),
                },
              ],
              itemDefinition: () => ({
                title: i18n.t('performance_process.agreements.page.title'),
                description: [
                  i18n.t('performance_process.agreements.agreement_list_card.click_to_start'),
                ],
                avatar: {
                  type: 'icon',
                  icon: Handshake,
                },
              }),
            },
          },
        ]}
      />
    </Box>
  )
}

interface TargetAgreementCardProps {
  target: PerformanceProcessTargetAgreementCardFragment
  reviewProcessId: number
  isCheckingOwnAgreement?: boolean
  onInitiate: () => void
}

export const TargetAgreementCard: React.FC<TargetAgreementCardProps> = ({
  target,
  reviewProcessId,
  isCheckingOwnAgreement,
  onInitiate,
}) => {
  const { navigation } = useNavigation()

  const agreement = target?.agreement
  const agreementDocument = agreement?.agreementDocuments[0]
  const documentId = agreementDocument?.documentsDocumentId

  const { data: esignaturesData } = useQuery(
    PerformanceProcessTargetAgreementCardEsignaturesDocument,
    {
      variables: { documentId: Number(documentId) },
      skip: !documentId,
    }
  )
  const esignatures = esignaturesData?.esignature.signaturesConnection.nodes ?? []

  const agreementPath = isCheckingOwnAgreement
    ? navigation.performanceProcess.reviews.review.myResultsAgreement.fullPath({
        targetId: target.id,
        reviewId: reviewProcessId,
        agreementId: agreement?.id ?? '',
      })
    : navigation.performanceProcess.reviews.review.target.agreement.fullPath({
        targetId: target.id,
        reviewId: reviewProcessId,
        agreementId: agreement?.id ?? '',
      })

  return (
    <Box paddingX="s24">
      {agreement ? (
        <AgreementCard agreement={agreement} esignatures={esignatures} url={agreementPath} />
      ) : (
        <NotInitiatedCard target={target} onInitiate={onInitiate} />
      )}
    </Box>
  )
}
